import { CompliancePageListAllocationComplianceQuery, FatInvestorsPageAllAdvisoryFirmListQuery } from '@/__generated__/graphql';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { Loader } from '@/components/loaders/loader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { SORT_ORDER, TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { allocationHistoryMobileTableSort, allocationHistoryTableColumns } from '../constants';
import { LIST_ALLOCATION_COMPLIANCE_QUERY } from '../queries';
import { ALLOCATION_COMPLIANCE_STATUSES } from '../types';
import { AllocationHistoryTable } from './table/table';
import AllocationHistoryMobileTable from './table/tableMobile';

export const AllocationHistory = () => {
  const goBack = useGoBack();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { searchParams, onChangeSort, onChangeSearch, onChangePrimaryFilter, onChangePaginationData, makeScroll, saveScrollPostion } =
    useSearchParamsManager();

  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;
  const backToTitle = searchParams.get('backToTitle') || '';
  const q = searchParams.get('q') || '';
  const quickFilter = searchParams.get('quickFilter') || '';
  const key = searchParams.get('key');
  const asc = searchParams.get('asc') === 'true' || false;
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const advisoryFirmQuickFilter = searchParams.get('advisoryFirmQuickFilter') || '';
  const position = searchParams.get('position') || 0;

  const [showModalLoader, setShowModalLoader] = useState(false);
  const [modalLoaderTitle, setModalLoaderTitle] = useState('');
  const [advisoryFirmQuickFilterList, setAdvisoryFirmQuickFilterList] = useState<ISelectValue[]>([]);
  const [tableColumns, setTableColumns] = useState<TableColumns[]>(allocationHistoryTableColumns);
  const [allocationHistoryTableRows, setAllocationHistoryTableRows] = useState<
    CompliancePageListAllocationComplianceQuery['ListAllocationCompliance']['allocations']
  >([]);
  const [mobileSortItems, setMobileSortItems] = useState(allocationHistoryMobileTableSort);
  const [mobileSort, setMobileSort] = useState(allocationHistoryMobileTableSort[6].title);
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  useQuery<{ allAdvisoryFirmList: FatInvestorsPageAllAdvisoryFirmListQuery['allAdvisoryFirmList'] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: !isPlatformManagerUser,
    onCompleted: ({ allAdvisoryFirmList }) => {
      setAdvisoryFirmQuickFilterList([{ label: 'All', value: '' }, ...new Set(allAdvisoryFirmList.map((el) => createSimpleSelectOption(el.name)))]);
    }
  });

  const {
    data: listAllocationComplianceData,
    loading: listAllocationComplianceLoading,
    refetch: listAllocationComplianceRefetch
  } = useQuery<{ ListAllocationCompliance: CompliancePageListAllocationComplianceQuery['ListAllocationCompliance'] }>(
    LIST_ALLOCATION_COMPLIANCE_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        advisoryFirmFilter: advisoryFirmQuickFilter,
        allocationStatusFilter: quickFilter,
        order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        sort: key || 'statusDate',
        search: q,
        rowsPerPage: Number(limit),
        page: Math.floor(Number(offset) / Number(limit)) + 1
      },
      onCompleted: (data) => {
        setAllocationHistoryTableRows(data.ListAllocationCompliance.allocations);
        makeScroll(position);
      }
    }
  );

  useEffect(() => {
    if (user && user?.tenant?.type === TenantType.ADVISORY_FIRM) {
      const updatedTableColumns = allocationHistoryTableColumns.map((column) => {
        if (column.key !== 'advisoryFirm') {
          return column;
        }
        return { ...column, width: 0, title: '' };
      });
      setTableColumns(updatedTableColumns);

      const updatedMobileSortItems = allocationHistoryMobileTableSort.filter((item) => item.key !== 'advisoryFirm');
      setMobileSortItems(updatedMobileSortItems);
    }
  }, [user]);

  useEffect(() => {
    if (!isMobile) return;

    listAllocationComplianceRefetch({
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sort: sortItem?.key
    });
  }, [mobileSort]);

  const openPage = (path: string) => {
    saveScrollPostion();
    navigate(path);
  };

  const viewSignaturePacket = async (id: string) => {
    if (!id) return;
    try {
      setModalLoaderTitle('View Signature Packet');
      setShowModalLoader(true);
      const { data, headers } = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/subscription-document/${id}/documents`,

        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([data], { type: headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalLoader(false);
    }
  };

  const handleExportData = async () => {
    try {
      setModalLoaderTitle('Export Data');
      setShowModalLoader(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/allocation/compliance-export`, {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      fileDownload(response.data, 'allocation-compliance.csv');
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalLoader(false);
    }
  };

  return (
    <>
      {showModalLoader && (
        <ModalWrappedContent
          title={modalLoaderTitle}
          isOpen={showModalLoader}
          onClose={() => {}}
          showCloseIcon={false}
          showCancelButton={false}
          isUseOutsideClick={false}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/compliance' })} backToTitle={backToTitle} />} />
        <PageTitle title="Allocation History" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search entity, investment, client'
            }}
            {...(isPlatformManagerUser && {
              advisoryFirmQuickFilter: {
                title: 'Advisory Firm',
                value: advisoryFirmQuickFilter,
                defaultValue: 'All',
                options: advisoryFirmQuickFilterList,
                onChange: (value) => onChangePrimaryFilter(value, 'advisoryFirmQuickFilter')
              }
            })}
            quickFilter={{
              title: 'Status',
              value: quickFilter,
              defaultValue: 'All',
              options: [{ label: 'All', value: '' }, ...Object.values(ALLOCATION_COMPLIANCE_STATUSES).map((value) => ({ label: value, value }))],
              onChange: (value) => onChangePrimaryFilter(value, 'quickFilter')
            }}
            resultsValue={listAllocationComplianceData?.ListAllocationCompliance?.total || 0}
            refetch={listAllocationComplianceRefetch}
            mobileSort={{ value: mobileSort, onChange: setMobileSort, selects: mobileSortList }}
            useNewFilters
            downloadButton={{ onClick: handleExportData }}
          />
          {isMobile ? (
            <>
              {listAllocationComplianceLoading ? (
                <Loader />
              ) : (
                <AllocationHistoryMobileTable
                  tableColumns={tableColumns}
                  tableRows={allocationHistoryTableRows}
                  openPage={openPage}
                  viewSignaturePacket={viewSignaturePacket}
                />
              )}
            </>
          ) : (
            <AllocationHistoryTable
              tableColumns={tableColumns}
              tableRows={allocationHistoryTableRows}
              sort={{ key, asc }}
              loading={listAllocationComplianceLoading}
              setSort={onChangeSort}
              openPage={openPage}
              viewSignaturePacket={viewSignaturePacket}
            />
          )}

          <PaginationWrap>
            <TablePagination
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: listAllocationComplianceData?.ListAllocationCompliance.total ?? 0
              }}
              savePagination={onChangePaginationData}
              refetch={() => {}}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
